// src/components/InstallmentModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import moment from "moment";
import api from "../../api/axios";
import { clearIdempotencyKey, getIdempotencyKey } from "../../utilities/idempotencyKey";

function calculateInstallments({
  dueAmount,
  minimumAmount,
  planType,
  startDate,
}) {
  // returns array of installments { sequence, dueDate, amount }
  const gapDays = planType === "monthly" ? 30 : planType === "weekly" ? 7 : 15;
  const totalInstallments = Math.max(1, Math.ceil(dueAmount / minimumAmount));
  // distribute equally (last one adjust)
  const base = parseFloat((dueAmount / totalInstallments).toFixed(2));
  const installments = [];
  let acc = 0;
  for (let i = 1; i <= totalInstallments; i++) {
    let amount = base;
    // last installment adjust rounding difference
    if (i === totalInstallments) {
      const sumPrev = base * (totalInstallments - 1);
      amount = parseFloat((dueAmount - sumPrev).toFixed(2));
    }
    const dueDate = moment(startDate)
      .add(gapDays * (i - 1), "days")
      .format("YYYY-MM-DD");
    installments.push({
      sequence: i,
      dueDate,
      amount,
    });
    acc += amount;
  }
  return installments;
}

export default function InstallmentModal({
  show,
  onHide,
  booking,
  refreshList,
}) {
  const isEdit = !!booking?.installments?.length;
  const existingInstallments = useMemo(
    () =>
      Array.isArray(booking?.installments)
        ? [...booking.installments].sort(
            (a, b) =>
              (a.installment_sequence || 0) - (b.installment_sequence || 0)
          )
        : [],
    [booking]
  );

  const existingSummary = useMemo(() => {
    if (!isEdit || existingInstallments.length === 0) return null;
    const first = existingInstallments[0];
    const last = existingInstallments[existingInstallments.length - 1];

    const planLabel = first?.plan_name || first?.plan_type || "";
    const typicalAmount =
      first?.installment_amount != null
        ? Number(first.installment_amount).toFixed(2)
        : null;

    return {
      planLabel,
      count: existingInstallments.length,
      typicalAmount,
      firstDue: first?.due_date,
      lastDue: last?.due_date,
    };
  }, [existingInstallments, isEdit]);

  const [planType, setPlanType] = useState("monthly");
  const [minInstallment, setMinInstallment] = useState(0);
  const [minimumAmount, setMinimumAmount] = useState(0);
  const [startDate, setStartDate] = useState(
    moment().add(3, "days").format("YYYY-MM-DD")
  );
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const payLock = useRef(false);
  const paymentKeyRef = useRef(null);
  const minimumPercent = localStorage.getItem("minimumPercent");
  console.log("Installment booking", booking);
  async function downloadReceipt(receiptId, invoiceNumber) {
    if (!receiptId) return;
    try {
      const res = await api.get(`/api/invoices/${receiptId}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Receipt-${invoiceNumber || receiptId}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Payment recorded, but receipt download failed.");
    }
  }

  async function handlePay(installment) {
    if (payLock.current) return;
    try {
      payLock.current = true;
      setLoading(true);

      const amount =
        Number(installment.due_amount ?? installment.amount ?? installment.installment_amount) ||
        0;

      if (amount <= 0) {
        throw new Error("Invalid payment amount");
      }

      const url = `/api/installments/${booking.id}/${installment.id}/pay`;
      const key = getIdempotencyKey(paymentKeyRef, JSON.stringify({ booking_id: booking.id, installment_id: installment.id, amount }));
      const res = await api.post(url, { amount }, { headers: { "Idempotency-Key": key } });

      if (res.data?.receipt?.id) {
        await downloadReceipt(
          res.data.receipt.id,
          res.data.receipt.invoice_number
        );
      }

      setLoading(false);
      clearIdempotencyKey(paymentKeyRef);
      onHide();

      if (refreshList) refreshList();
    } catch (e) {
      setLoading(false);
      const message = e?.response?.data?.message || "Installment payment could not be submitted. Retry the same installment to safely continue.";
      setError(message);
      alert(message);
    } finally {
      payLock.current = false;
    }
  }

  useEffect(() => {
    if (booking) {
      // set default minInstallment to something like 1/8 of due or 5000
      const defaultMin = Math.max(1, Math.round(booking.due_amount / 8));
      setMinInstallment(defaultMin);
    } else {
      return;
    }
  }, [booking]);

  // useEffect(() => {
  //   if (!booking) return;
  //   const p = calculateInstallments({
  //     dueAmount: parseFloat(booking.due_amount),
  //     minInstallment: parseFloat(minInstallment) || 1,
  //     planType,
  //     startDate,
  //   });
  //  // setPreview(p);
  // }, [booking, minInstallment, planType, startDate]);

  const totalPreviewAmount = useMemo(
    () => preview.reduce((s, it) => s + Number(it.amount), 0).toFixed(2),
    [preview]
  );

  function renderExistingInstallmentsTable() {
    if (!isEdit || existingInstallments.length === 0) return null;

    return (
      <>
        <h6>Current Installment Plan</h6>
        {existingSummary && (
          <div className="small text-muted mb-2">
            {existingSummary.planLabel && (
              <>Type: {existingSummary.planLabel} | </>
            )}
            Installments: {existingSummary.count}
            {existingSummary.typicalAmount && (
              <> | Typical amount: {existingSummary.typicalAmount}</>
            )}
            {existingSummary.firstDue && existingSummary.lastDue && (
              <>
                {" "}
                | Range: {existingSummary.firstDue} → {existingSummary.lastDue}
              </>
            )}
          </div>
        )}

        <div className="table-responsive mb-3">
          <table className="table table-sm table-bordered">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Due Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {existingInstallments.map((inst) => {
                const isPaid = inst.status === "paid" || inst.is_paid === 1;
                const status = inst.status || (isPaid ? "paid" : "pending");
                const dueAmount = Number(
                  inst.due_amount ?? inst.installment_amount ?? 0
                );

                const badgeClass = isPaid
                  ? "badge bg-success"
                  : status === "overdue"
                  ? "badge bg-danger"
                  : "badge bg-secondary";

                const rowClass = isPaid ? "table-success" : "";

                return (
                  <tr key={inst.id} className={rowClass}>
                    <td>{inst.installment_sequence}</td>
                    <td>{inst.due_date}</td>
                    <td>{Number(inst.installment_amount).toFixed(2)}</td>
                    <td>
                      <span className={badgeClass}>{status}</span>
                    </td>
                    <td>
                      {!isPaid && dueAmount > 0 && (
                        <Button
                          size="sm"
                          variant="outline-success"
                          disabled={loading}
                          onClick={() =>
                            handlePay({
                              ...inst,
                              amount: dueAmount,
                              due_amount: dueAmount,
                            })
                          }
                        >
                          Pay
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  async function handleGenerate() {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        plan_type: planType,
        minimumAmount: minimumAmount,
        start_date: startDate,
      };
      const url = isEdit
        ? `/api/installments/update/${booking.id}`
        : `/api/installments/generate/${booking.id}`;

      const res = await api.post(url, payload);
      // success
      setLoading(false);
      onHide();
      if (refreshList) refreshList();
    } catch (e) {
      setLoading(false);
      const msg = e?.response?.data?.message || e.message || "Server error";
      setError(msg);
    }
  }

  function renderPreviewTable() {
    return (
      <div className="table-responsive">
        <table className="table table-sm table-bordered">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Due Date</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {preview
              .filter((p) => !p.is_paid) //  paid
              .map((p, index) => (
                <tr key={p.id}>
                  <td>{p.sequence}</td>
                  <td>{p.dueDate}</td>
                  <td>{p.amount.toFixed(2)}</td>
                  {/* <td>
                    {index === 0 && ( //   unpaid installment এ Pay button
                      <Button size="sm" onClick={() => handlePay(p)}>
                        Pay
                      </Button>
                    )}
                  </td> */}
                </tr>
              ))}

            <tr>
              <td colSpan={2} className="text-end">
                <strong>Total</strong>
              </td>
              <td>
                <strong>{totalPreviewAmount}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit
            ? `Edit Installment Plan for Booking #${booking?.id}`
            : `Generate Installment Plan for Booking #${booking?.id}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {booking ? (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <div className="border p-2">
                  <div className="small text-muted">Customer</div>
                  <div>
                    <strong>
                      {booking.user?.name || booking.payer_name || "N/A"}
                    </strong>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="border p-2">
                  <div className="small text-muted">Total Amount</div>
                  <div>
                    <strong>{Number(booking.total_amount).toFixed(2)}</strong>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="border p-2">
                  <div className="small text-muted">Due Amount</div>
                  <div>
                    <strong
                      className={
                        booking.due_amount > 0 ? "text-danger" : "text-success"
                      }
                    >
                      {Number(booking.due_amount).toFixed(2)}
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            {isEdit && renderExistingInstallmentsTable()}

            <Form>
              <div className="row g-2 align-items-end">
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>Installment Type</Form.Label>
                    <Form.Select
                      value={planType}
                      onChange={(e) => setPlanType(e.target.value)}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="weekly">Weekly</option>
                      <option value="15days">Every 15 days</option>
                    </Form.Select>
                    <div className="small text-muted">Installment Type</div>
                  </Form.Group>
                </div>

                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Minimum Installment</Form.Label>
                    <Form.Control
                      type="text"
                      value={minimumAmount}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        if (!isNaN(val)) {
                          setMinimumAmount(val);
                        }
                      }}
                    />
                    <div className="small text-muted">
                      first installment amount
                    </div>
                  </Form.Group>
                </div>

                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                    <div className="small text-muted">
                      first installment due
                    </div>
                  </Form.Group>
                </div>

                <div className="col-md-2 text-end">
                  <Button
                    variant="primary"
                    onClick={() => {
                      const p = calculateInstallments({
                        dueAmount: parseFloat(booking.due_amount),
                        minimumAmount: minimumAmount
                          ? parseFloat(minimumAmount)
                          : parseFloat(booking.due_amount),
                        planType,
                        startDate,
                      });

                      //
                      const previewWithId = p.map((item, idx) => ({
                        ...item,
                        id:
                          booking.installments?.[idx]?.id || `temp-${idx + 1}`,
                      }));
                      console.log(
                        " calculateInstallments",
                        p,
                        "Preview with IDs:",
                        previewWithId
                      );
                      setPreview(previewWithId);
                    }}
                  >
                    Preview
                  </Button>
                </div>
              </div>
            </Form>

            <hr />

            <h6>Preview Schedule</h6>
            {renderPreviewTable()}

            {error && <div className="alert alert-danger mt-2">{error}</div>}
          </>
        ) : (
          <div>Loading booking...</div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Close
        </Button>
        <Button
          variant="success"
          onClick={handleGenerate}
          disabled={loading || preview.length === 0}
        >
          {loading
            ? isEdit
              ? "Updating..."
              : "Generating..."
            : isEdit
            ? "Update Installments"
            : "Generate Installments"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
